import { MouseEvent, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Button, FoodsIconButton, Typography } from "@foodpilot/foods";
import ActionsPopover, { ActionsPopoverAppearance } from "src/components/ActionsPopover/ActionsPopover";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";

export type TableAction = Omit<MenuItemType, "onClick"> & {
    onClick?: () => void;
    subActions?: MenuItemType[];
    renderPopover?: (anchor: HTMLElement, onClose: () => void) => ReactNode;
    isBusy?: boolean;
};

type GridTableActionsProps = {
    actions: TableAction[];
    appearance: ActionsPopoverAppearance;
};

const isVisible = (action: TableAction) => action.shouldShow?.() ?? true;

export const GridTableActions = (props: GridTableActionsProps) => {
    const { actions, appearance } = props;
    const { t } = useTranslation();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [isMenuOpen, setMenuOpen] = useState(false);
    const [openedActionIndex, setOpenedActionIndex] = useState<number | null>(null);

    const visibleActions = actions.filter(isVisible);
    if (visibleActions.length === 0) return null;

    const openedAction = openedActionIndex === null ? undefined : visibleActions[openedActionIndex];
    const isOpenedActionGone = openedActionIndex !== null && openedAction === undefined;
    const menuAnchor = isMenuOpen && !isOpenedActionGone ? anchor : null;
    const isOpen = menuAnchor !== null || (anchor !== null && openedAction?.renderPopover !== undefined);

    const close = () => {
        setAnchor(null);
        setMenuOpen(false);
        setOpenedActionIndex(null);
    };

    const closePopover = () => {
        anchor?.focus();
        close();
    };

    const runAndClose = (run: () => void) => () => {
        close();
        run();
    };

    const open = (index: number) => {
        setOpenedActionIndex(index);
        setMenuOpen(visibleActions[index].renderPopover === undefined);
    };

    const subActionItems = (action: TableAction): MenuItemType[] =>
        (action.subActions ?? []).map((subAction) => ({
            ...subAction,
            onClick: runAndClose(() => subAction.onClick(undefined)),
        }));

    const actionItems: MenuItemType[] = visibleActions.map((action, index) => ({
        icon: action.icon,
        label: action.label,
        isDisabled: action.isDisabled,
        onClick: () => {
            if (action.subActions !== undefined || action.renderPopover !== undefined) {
                open(index);
                return;
            }
            runAndClose(() => action.onClick?.())();
        },
    }));

    const overlays = (
        <>
            <ActionsPopover
                key={openedAction?.subActions === undefined ? "actions" : `sub-actions-${openedActionIndex}`}
                appearance={appearance}
                menuItems={openedAction?.subActions === undefined ? actionItems : subActionItems(openedAction)}
                anchor={menuAnchor}
                closeWindow={close}
            />
            {anchor !== null && !isMenuOpen && openedAction?.renderPopover?.(anchor, closePopover)}
        </>
    );

    if (visibleActions.length === 1) {
        const [action] = visibleActions;
        const popupKind =
            action.subActions !== undefined ? "menu"
            : action.renderPopover !== undefined ? "dialog"
            : undefined;
        const onButtonClick = (event: MouseEvent<HTMLElement>) => {
            if (popupKind === undefined) {
                action.onClick?.();
                return;
            }
            setAnchor(event.currentTarget);
            open(0);
        };

        return (
            <Box flexShrink={0}>
                <Button
                    variant="ternary"
                    startIcon={action.icon}
                    onClick={onButtonClick}
                    disabled={Boolean(action.isDisabled)}
                    aria-haspopup={popupKind}
                    aria-expanded={popupKind === undefined ? undefined : isOpen}
                    aria-busy={action.isBusy === true ? true : undefined}
                >
                    <Typography variant="body-bold">{action.label}</Typography>
                </Button>
                {overlays}
            </Box>
        );
    }

    const buttonProps = {
        "aria-label": t("grid.table_actions.more", "More actions"),
        "aria-haspopup": "menu" as const,
        "aria-expanded": isOpen,
        "aria-busy": visibleActions.some((action) => action.isBusy === true) ? true : undefined,
        onClick: (event: MouseEvent<HTMLElement>) => {
            setAnchor(event.currentTarget);
            setOpenedActionIndex(null);
            setMenuOpen(true);
        },
    };

    return (
        <Box flexShrink={0}>
            {appearance === "themed" ?
                <FoodsIconButton icon="actions" size="s" withBackground buttonProps={buttonProps} />
            :   <IconButton {...buttonProps}>
                    <MoreHorizIcon />
                </IconButton>
            }
            {overlays}
        </Box>
    );
};
